import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Download, Search } from 'lucide-react';
import ReportHeader from '@/components/ReportHeader';
import { apiService } from '@/services/api';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';

const Container = styled.div`
  padding: 16px;
`;

const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  min-width: 320px;
`;

const Select = styled.select`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  min-width: 220px;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const Acoes = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const Botao = styled.button`
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const Hint = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
`;

const ScrollTop = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  height: 16px;
  margin: 0 16px 8px 16px;
`;

const ScrollBottom = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`;

type LinhaRelatorio = {
  Filial: string;
  NC: string;
  DataVenda: string;
  Cliente: string;
  Produto: string;
  Grupo: string;
  Medida: string;
  Quantidade: number;
};

const COLUNAS = ['Filial', 'NC', 'DataVenda', 'Cliente', 'Produto', 'Grupo', 'Medida', 'Quantidade'] as const;

const normalizarListaNcs = (valor: string): string[] => {
  const itens = valor
    .split(',')
    .map((item) => item.replace(/\s+/g, '').trim())
    .filter((item) => /^\d+$/.test(item));

  return Array.from(new Set(itens));
};

const formatarDataBR = (valor: string): string => {
  if (!valor) return '';
  const match = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(valor);
  return `${match[3]}/${match[2]}/${match[1]}`;
};

const RelatorioPneusPorNc: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const hoje = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [dataInicio, setDataInicio] = useState<string>(hoje);
  const [dataFim, setDataFim] = useState<string>(hoje);
  const [ncInput, setNcInput] = useState<string>('');
  const [produtoInput, setProdutoInput] = useState<string>('');
  const [clienteInput, setClienteInput] = useState<string>('');
  const [grupo, setGrupo] = useState<string>('PNEUS');
  const [ncAplicada, setNcAplicada] = useState<string>('');
  const [produtoAplicado, setProdutoAplicado] = useState<string>('');
  const [clienteAplicado, setClienteAplicado] = useState<string>('');
  const [grupoAplicado, setGrupoAplicado] = useState<string>('PNEUS');
  const [dataInicioAplicada, setDataInicioAplicada] = useState<string>(hoje);
  const [dataFimAplicada, setDataFimAplicada] = useState<string>(hoje);
  const [todasFiliais, setTodasFiliais] = useState<boolean>(false);
  const [grupos, setGrupos] = useState<Array<{ Controle: number; Grupo: string }>>([]);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const [contentWidth, setContentWidth] = useState<number>(0);

  const ncsConsultadas = useMemo(() => normalizarListaNcs(ncAplicada), [ncAplicada]);
  const filtrosProntos = todasFiliais || !!filialSelecionada?.id;

  const queryKey = useMemo(() => (
    [
      'relatorio-pneus-por-nc',
      todasFiliais ? 'todas' : (filialSelecionada?.id || '-'),
      dataInicioAplicada,
      dataFimAplicada,
      ncAplicada,
      produtoAplicado,
      clienteAplicado,
      grupoAplicado
    ]
  ), [todasFiliais, filialSelecionada?.id, dataInicioAplicada, dataFimAplicada, ncAplicada, produtoAplicado, clienteAplicado, grupoAplicado]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey,
    enabled: filtrosProntos,
    queryFn: async () => {
      const filtros = {
        dataInicio: dataInicioAplicada,
        dataFim: dataFimAplicada,
        NC: ncsConsultadas.join(','),
        produto: produtoAplicado.trim(),
        cliente: clienteAplicado.trim(),
        grupo: grupoAplicado
      };
      if (todasFiliais) return apiService.obterPneusPorNcTodas(filtros);
      if (!filialSelecionada?.id) return null;
      return apiService.obterPneusPorNcFilial(filialSelecionada.id, filtros);
    }
  });

  useEffect(() => {
    const carregar = async () => {
      if (!filialSelecionada?.id) return;
      try {
        const lista = await apiService.listarGrupos(filialSelecionada.id);
        setGrupos(lista);
      } catch {
        // toast global ja trata erro de API
      }
    };
    carregar();
  }, [filialSelecionada?.id]);

  const linhas = useMemo<LinhaRelatorio[]>(() => {
    const dados = (data?.data?.dados || []) as LinhaRelatorio[];
    return dados;
  }, [data?.data?.dados]);

  useEffect(() => {
    const bottom = bottomScrollRef.current;
    if (!bottom) return;
    const update = () => setContentWidth(bottom.scrollWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [linhas]);

  const handleTopScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && bottom.scrollLeft !== top.scrollLeft) {
      bottom.scrollLeft = top.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && top.scrollLeft !== bottom.scrollLeft) {
      top.scrollLeft = bottom.scrollLeft;
    }
  };

  const handleAplicar = () => {
    setDataInicioAplicada(dataInicio);
    setDataFimAplicada(dataFim);
    setNcAplicada(normalizarListaNcs(ncInput).join(','));
    setProdutoAplicado(produtoInput.trim());
    setClienteAplicado(clienteInput.trim());
    setGrupoAplicado(grupo);
  };

  const handleExportar = () => {
    if (linhas.length === 0) return;

    const sep = ';';
    const header = COLUNAS.join(sep);
    const body = linhas.map((linha) => {
      return COLUNAS.map((coluna) => {
        const valor = coluna === 'DataVenda'
          ? formatarDataBR(String(linha[coluna] ?? ''))
          : String(linha[coluna] ?? '');
        const precisaAspas = valor.includes('"') || valor.includes(sep) || /[\n\r]/.test(valor);
        const escaped = valor.replace(/"/g, '""');
        return precisaAspas ? `"${escaped}"` : escaped;
      }).join(sep);
    }).join('\n');

    const contexto = todasFiliais ? 'todas' : (filialSelecionada?.nome?.replace(/[^\w\-\s]/g, '') || 'filial');
    const nomeArquivo = `vendas_por_nc_${contexto}_${dataInicioAplicada}_a_${dataFimAplicada}.csv`;
    const csvContent = `\ufeff${header}\n${body}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Container>
      <ReportHeader
        title="Vendas por NC"
        subtitle={`${linhas.length} linhas retornadas no período de ${formatarDataBR(dataInicioAplicada)} a ${formatarDataBR(dataFimAplicada)}`}
        rightActions={(
          <Acoes>
            <Botao onClick={handleExportar} disabled={isLoading || isFetching || linhas.length === 0}>
              <Download size={16} /> Exportar
            </Botao>
            <Botao onClick={handleAplicar} disabled={isLoading || isFetching || (!todasFiliais && !filialSelecionada?.id)}>
              <Search size={16} /> Aplicar
            </Botao>
          </Acoes>
        )}
      />

      <Filtros>
        <Campo>
          <Label>Data Início</Label>
          <Input
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
          />
        </Campo>
        <Campo>
          <Label>Data Fim</Label>
          <Input
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
          />
        </Campo>
        <Campo>
          <Label>NC</Label>
          <Input
            type="text"
            placeholder="Ex: 12345, 67890"
            value={ncInput}
            onChange={(e) => setNcInput(e.target.value)}
          />
          <Hint>Você pode informar uma NC ou várias separadas por vírgula. Espaços são ignorados.</Hint>
        </Campo>
        <Campo>
          <Label>Produto</Label>
          <Input
            type="text"
            placeholder="Ex: 175/70 R13"
            value={produtoInput}
            onChange={(e) => setProdutoInput(e.target.value)}
          />
        </Campo>
        <Campo>
          <Label>Grupo</Label>
          <Select value={grupo} onChange={(e) => setGrupo(e.target.value)}>
            <option value="todos">Todos</option>
            <option value="PNEUS">PNEUS</option>
            {grupos
              .filter((item) => {
                const nome = String(item.Grupo || '').trim();
                return nome !== '' && nome.toUpperCase() !== 'PNEUS';
              })
              .map((item) => (
                <option key={`${item.Controle}-${item.Grupo}`} value={item.Grupo}>
                  {item.Grupo}
                </option>
              ))}
          </Select>
        </Campo>
        <Campo>
          <Label>Cliente</Label>
          <Input
            type="text"
            placeholder="Nome do cliente"
            value={clienteInput}
            onChange={(e) => setClienteInput(e.target.value)}
          />
        </Campo>
        <Campo>
          <Label>&nbsp;</Label>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={todasFiliais}
              onChange={(e) => setTodasFiliais(e.target.checked)}
            />
            Todas as Filiais
          </CheckboxRow>
        </Campo>
      </Filtros>

      <ScrollTop ref={topScrollRef} onScroll={handleTopScroll}>
        <div style={{ width: contentWidth }} />
      </ScrollTop>

      <ScrollBottom ref={bottomScrollRef} onScroll={handleBottomScroll}>
        <Tabela>
          <thead>
            <tr>
              {COLUNAS.map((coluna) => (
                <Th key={coluna}>{coluna === 'DataVenda' ? 'Data da Venda' : coluna}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><Td colSpan={COLUNAS.length}>Carregando...</Td></tr>
            ) : linhas.length === 0 ? (
              <tr><Td colSpan={COLUNAS.length}>Nenhum registro encontrado</Td></tr>
            ) : (
              linhas.map((linha, idx) => (
                <tr key={`${linha.Filial}-${linha.NC}-${linha.Produto}-${idx}`}>
                  <Td>{linha.Filial}</Td>
                  <Td>{linha.NC}</Td>
                  <Td>{formatarDataBR(linha.DataVenda)}</Td>
                  <Td>{linha.Cliente}</Td>
                  <Td>{linha.Produto}</Td>
                  <Td>{linha.Grupo}</Td>
                  <Td>{linha.Medida}</Td>
                  <Td>{Number(linha.Quantidade || 0).toLocaleString('pt-BR')}</Td>
                </tr>
              ))
            )}
          </tbody>
        </Tabela>
      </ScrollBottom>
    </Container>
  );
};

export default RelatorioPneusPorNc;
